import React from "react";
import {
  Box,
  Typography,
  Step,
  StepLabel,
  Stepper as MuiStepper,
  StepConnector,
  styled,
} from "@mui/material";
import { Check } from "@mui/icons-material";
import theme from "config/themes";

interface StepperProps {
  currentStep: number;
}

interface CustomStep {
  id: number;
  label: string;
  completed: boolean;
}

const CustomStepConnector = styled(StepConnector)(({ theme }) => ({
  "&.MuiStepConnector-alternativeLabel": {
    top: 10,
    left: "calc(-50% + 16px)",
    right: "calc(50% + 16px)",
  },
  "&.MuiStepConnector-vertical": {
    padding: 0,
    marginLeft: 12,
    "& .MuiStepConnector-line": {
      borderColor: theme.palette.grey[300],
      borderLeftWidth: 2,
      minHeight: 32,
    },
  },
  "&.MuiStepConnector-active .MuiStepConnector-line": {
    borderColor: theme.palette.success.main,
  },
  "&.MuiStepConnector-completed .MuiStepConnector-line": {
    borderColor: theme.palette.success.main,
  },
}));

const CustomStepIcon = styled("div")<{
  ownerState: { completed: boolean; active: boolean };
}>(({ theme, ownerState }) => ({
  backgroundColor: ownerState.completed
    ? theme.palette.success.main
    : ownerState.active
    ? theme.palette.grey[500]
    : theme.palette.grey[300],
  zIndex: 1,
  color: "#fff",
  width: 24,
  height: 24,
  display: "flex",
  borderRadius: "50%",
  justifyContent: "center",
  alignItems: "center",
  fontSize: "16px",
  border:
    ownerState.completed || ownerState.active
      ? "none"
      : `2px solid ${theme.palette.grey[300]}`,
}));

function StepIcon(props: { active: boolean; completed: boolean }) {
  const { active, completed } = props;

  return (
    <CustomStepIcon ownerState={{ completed, active }}>
      {completed ? (
        <Check sx={{ fontSize: 16 }} />
      ) : (
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: active ? "white" : "transparent",
          }}
        />
      )}
    </CustomStepIcon>
  );
}

const Stepper: React.FC<StepperProps> = ({ currentStep }) => {
  const steps: CustomStep[] = [
    {
      id: 1,
      label: "Tell Us About Yourself",
      completed: currentStep > 1,
    },
    {
      id: 2,
      label: "Upload Your Catalog",
      completed: currentStep > 2,
    },
    {
      id: 3,
      label: "Verify your Information",
      completed: currentStep > 3,
    },
    {
      id: 4,
      label: "Explore the Portal",
      completed: currentStep > 4,
    },
  ];

  return (
    <Box
      sx={{
        backgroundImage: `
          linear-gradient(0deg, rgba(255,255,255,0.55), rgba(255,255,255,0.55)),
          linear-gradient(135deg, ${theme.palette.customColor4.color} 0%, ${theme.palette.customColor2.color} 100%)
        `,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        padding: 4,
        minHeight: "100vh",
        width: 300,
        borderRight: 1,
        borderColor: "grey.200",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontSize: 18,
          fontWeight: 600,
          color: "text.primary",
          mb: 4,
          mt: 0,
        }}
      >
        Setup Progress
      </Typography>

      <MuiStepper
        activeStep={currentStep - 1}
        orientation="vertical"
        connector={<CustomStepConnector />}
        sx={{
          "& .MuiStepLabel-root": {
            padding: 0,
          },
          "& .MuiStepContent-root": {
            borderLeft: "none",
            marginLeft: 0,
            paddingLeft: 0,
          },
        }}
      >
        {steps.map((step, index) => (
          <Step key={step.id} completed={step.completed}>
            <StepLabel
              StepIconComponent={() => (
                <StepIcon
                  active={currentStep === step.id}
                  completed={step.completed}
                />
              )}
              sx={{
                "& .MuiStepLabel-labelContainer": {
                  ml: 2,
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: currentStep === step.id ? 600 : 400,
                  color:
                    step.completed || currentStep === step.id
                      ? "text.primary"
                      : "text.secondary",
                }}
              >
                {step.label}
              </Typography>
            </StepLabel>
          </Step>
        ))}
      </MuiStepper>
    </Box>
  );
};

export default Stepper;
